import { Spacing } from '@persona-id/design-tokens';
import { CircleHelpIcon } from '@persona-id/design-tokens/icons';
import styled from 'styled-components';

import ControlPanelContainer from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/ControlPanelContainer';
import ControlPanelDivider from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/ControlPanelDivider';
import ReactSelect from 'dashboard/components/ReactSelect';
import { AccountTypesFilterOption } from 'dashboard/models/account-type';
import { ModuleLayoutByAccount } from 'dashboard/modules/LayoutByAccount';
import Checkbox from 'design-system/components/Checkbox';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';

const LabelAndTooltipContainer = styled('div')`
  display: flex;
  flex-direction: horizontal;
  gap: ${Spacing[1]};
  align-items: center;
`;

const TooltipContainer = styled('div')`
  max-width: 500px;
`;

export interface LayoutByAccountPanelProps {
  module: ModuleLayoutByAccount;
  accountTypeFilterOptions: AccountTypesFilterOption[];
  updateModule: (module: ModuleLayoutByAccount) => void;
}

const LayoutByAccountPanel = ({
  module,
  accountTypeFilterOptions,
  updateModule,
}: LayoutByAccountPanelProps) => {
  const handleSetAccountType = (accountType: ModuleLayoutByAccount['account-type']) => {
    updateModule({
      ...module,
      'account-type': accountType,
    });
  };

  const handleSetGroupTitleFieldKey = (
    groupTitleFieldKey: ModuleLayoutByAccount['group-title-field-key'],
  ) => {
    updateModule({
      ...module,
      'group-title-field-key': groupTitleFieldKey,
    });
  };

  const handleSetShowStatusInTitle = (
    showStatusInTitle: ModuleLayoutByAccount['show-status-in-title'],
  ) => {
    updateModule({
      ...module,
      'show-status-in-title': showStatusInTitle,
    });
  };

  const accountTypeOptions = [
    {
      label: 'Any',
      value: '',
    },
    ...accountTypeFilterOptions
      .map((accountTypeFilterOption) => {
        return {
          label: accountTypeFilterOption.name,
          value: accountTypeFilterOption.id,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  return (
    <>
      <ControlPanelContainer>
        <Heading size='sm'>Settings</Heading>
        <Spacer height={2} />
        <LabelAndTooltipContainer>
          <Label id='account-type-selector-label' size='md' weight='bold'>
            Account type
          </Label>
          <Tooltip side='bottom' iconTrigger={<CircleHelpIcon />}>
            <TooltipContainer>
              <Paragraph>Choose which account types to display in the module.</Paragraph>
            </TooltipContainer>
          </Tooltip>
        </LabelAndTooltipContainer>
        <ReactSelect
          id='layout-by-account-panel__account-type-selector'
          aria-labelledby='account-type-selector-label'
          options={accountTypeOptions}
          value={module['account-type'] ?? ''}
          onChange={handleSetAccountType}
          portal
        />
        <Spacer height={2} />
        <LabelAndTooltipContainer>
          <Label id='group-title-field-key-input-label' size='md' weight='bold'>
            Title field key
          </Label>
          <Tooltip side='bottom' iconTrigger={<CircleHelpIcon />}>
            <TooltipContainer>
              <Paragraph>
                Determines which account field (e.g. business_name) is used as the header text for
                each account grouping. Account full name is the default if nothing is filled in.
              </Paragraph>
            </TooltipContainer>
          </Tooltip>
        </LabelAndTooltipContainer>
        <Input
          type='text'
          id='layout-by-account-panel__group-title-field-key-input'
          aria-labelledby='group-title-field-key-input-label'
          value={module['group-title-field-key'] ?? ''}
          onChange={(e) => handleSetGroupTitleFieldKey(e.target.value)}
        />

        <Spacer height={2} />
        <Checkbox
          label={<Paragraph>Show account status</Paragraph>}
          checked={module['show-status-in-title'] ?? false}
          onChange={(e) => {
            handleSetShowStatusInTitle(e.target.checked);
          }}
        />
      </ControlPanelContainer>
      <ControlPanelDivider full />
    </>
  );
};

export default LayoutByAccountPanel;
